import type { ChatType } from '@kanbu/schema';
import { AgentType } from '@kanbu/schema/enums';
import {
  CHAT_IMAGE_UPLOAD_LIMITS,
  THEME_PRESETS,
  AgentDefaults,
} from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { Button, Label } from '@utima/ui';
import { useFieldState, useFormApi } from 'informed';
import { Moon, Sun } from 'lucide-react';
import { memo } from 'react';

import { SimpleTooltip } from '@/components';
import { Card } from '@/components/card/Card';
import type { ScopedLazyFileUploadAdapter } from '@/components/fileUpload/adapters/lazyFileUploadAdapter';
import { SingleFileUpload } from '@/components/fileUpload/SingleFileUpload';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';

export type ChatThemeFieldsProps = {
  data?: ChatType;
  avatarUploadAdapter: ScopedLazyFileUploadAdapter;
  logoUploadAdapter: ScopedLazyFileUploadAdapter;
  bubbleUploadAdapter: ScopedLazyFileUploadAdapter;
};

export const ChatThemeFields = memo(function ChatThemeFields({
  avatarUploadAdapter,
  bubbleUploadAdapter,
  logoUploadAdapter,
}: ChatThemeFieldsProps) {
  const { t } = useLingui();
  const { setValue } = useFormApi();
  const typeFieldState = useFieldState('agentType');

  const { schema } = useFieldsSchema(
    [
      {
        name: 'themeConfig.preset',
        uiControl: 'hidden',
        defaultValue: AgentDefaults.defaultThemePreset,
      },
      {
        name: 'themeConfig.primary',
        label: t`Primary background`,
        showOptional: false,
        uiProps: {
          type: 'color',
        },
        defaultValue: AgentDefaults.defaultTheme.primary,
      },
      {
        name: 'themeConfig.primaryFg',
        label: t`Primary foreground`,
        showOptional: false,
        uiProps: {
          type: 'color',
        },
        defaultValue: AgentDefaults.defaultTheme.primaryFg,
      },
    ],
    [t],
  );

  const handleThemeChange = (selectedValue: keyof typeof THEME_PRESETS) => {
    if (selectedValue in THEME_PRESETS) {
      // Get the preset colors
      const preset = THEME_PRESETS[selectedValue as keyof typeof THEME_PRESETS];

      setValue('themeConfig.primary', preset.primary);
      setValue('themeConfig.primaryFg', preset.primaryFg);
      setValue('themeConfig.preset', selectedValue);
    }
  };

  // These fields should be visible only if the agent type is Chat
  if (typeFieldState.value !== AgentType.Chat) {
    return null;
  }

  return (
    <Card title={t`Theme config`}>
      <div className='flex flex-col gap-y-2'>
        <Label>{t`Style`}</Label>
        <div className='flex gap-x-2'>
          <SimpleTooltip delayDuration={0} title={t`Dark`}>
            <Button
              data-cta='admin-chat-theme-dark-button'
              variant='ghost'
              outline
              onClick={() => handleThemeChange('dark')}
            >
              <Moon />
            </Button>
          </SimpleTooltip>
          <SimpleTooltip delayDuration={0} title={t`Light`}>
            <Button
              data-cta='admin-chat-theme-light-button'
              variant='ghost'
              outline
              onClick={() => handleThemeChange('light')}
            >
              <Sun />
            </Button>
          </SimpleTooltip>
        </div>
      </div>
      <SchemaFields schema={schema} />
      <SingleFileUpload
        name='chatbotConfig.systemAvatar'
        label={t`System avatar`}
        tooltip={t`Upload a small square image (${CHAT_IMAGE_UPLOAD_LIMITS.avatar.recommendedSize}). Larger images slow down the chat widget.`}
        maxSize={CHAT_IMAGE_UPLOAD_LIMITS.avatar.maxFileSize}
        recommendedImageWidth={CHAT_IMAGE_UPLOAD_LIMITS.avatar.recommendedWidth}
        recommendedImageHeight={
          CHAT_IMAGE_UPLOAD_LIMITS.avatar.recommendedHeight
        }
        uploadAdapter={avatarUploadAdapter}
      />
      <SingleFileUpload
        name='themeConfig.logo'
        label={t`Header logo`}
        tooltip={t`Upload a wide logo (${CHAT_IMAGE_UPLOAD_LIMITS.header.recommendedSize}). It is displayed at 20 px height in the chat header.`}
        maxSize={CHAT_IMAGE_UPLOAD_LIMITS.header.maxFileSize}
        recommendedImageWidth={CHAT_IMAGE_UPLOAD_LIMITS.header.recommendedWidth}
        recommendedImageHeight={
          CHAT_IMAGE_UPLOAD_LIMITS.header.recommendedHeight
        }
        uploadAdapter={logoUploadAdapter}
      />
      <SingleFileUpload
        name='themeConfig.logoBubble'
        label={t`Logo bubble`}
        tooltip={t`Upload a square image (${CHAT_IMAGE_UPLOAD_LIMITS.bubble.recommendedSize}) for the chat launcher bubble.`}
        maxSize={CHAT_IMAGE_UPLOAD_LIMITS.bubble.maxFileSize}
        recommendedImageWidth={CHAT_IMAGE_UPLOAD_LIMITS.bubble.recommendedWidth}
        recommendedImageHeight={
          CHAT_IMAGE_UPLOAD_LIMITS.bubble.recommendedHeight
        }
        uploadAdapter={bubbleUploadAdapter}
      />
    </Card>
  );
});
